import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap';
import { Button } from '../layout/buttons';
import { createCompany } from '../../utils/companies';
import { logger } from '../../utils/logger';
import styles from './Canvasses.module.css';

const CompanyCreationButton = ({ onSuccess, selectedCompanyId }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdCompany, setCreatedCompany] = useState(null);
  const [companyData, setCompanyData] = useState({
    name: '',
    url: '',
    objective: '',
    industry: '',
    isPublic: false,
    metadata: {
      size: '',
      location: '',
      sector: ''
    }
  });

  useEffect(() => {
    // Reset created company if a different company is selected
    if (createdCompany && selectedCompanyId !== createdCompany.id) {
      setCreatedCompany(null);
    }
  }, [selectedCompanyId, createdCompany]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (!companyData.name.trim()) {
        setError('Company name is required');
        return;
      }

      const result = await createCompany(companyData);
      
      if (result.success) {
        setShowModal(false);
        setCompanyData({
          name: '',
          url: '',
          objective: '',
          industry: '',
          isPublic: false,
          metadata: {
            size: '',
            location: '',
            sector: ''
          }
        });
        
        // Store the created company for display
        setCreatedCompany(result.company);
        
        if (onSuccess) {
          onSuccess(result.company);
        }
      } else {
        setError(result.message || 'Failed to create company');
      }
    } catch (err) {
      logger.error('Error creating company:', err);
      setError('Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setCreatedCompany(null);
    setCompanyData({
      name: '',
      url: '',
      objective: '',
      industry: '',
      isPublic: false,
      metadata: {
        size: '',
        location: '',
        sector: ''
      }
    });
  };

  return (
    <>
      {createdCompany ? (
        // Show created company as a tile
        <div 
          className={`${styles.companyItem} ${styles.selectedItem}`}
          onClick={() => onSuccess && onSuccess(createdCompany)}
        >
          <div className={styles.companyName}>
            <strong>{createdCompany.name}</strong>
            {createdCompany.url && (
              <a href={createdCompany.url} target="_blank" rel="noopener noreferrer" className={styles.companyUrl}>
                {createdCompany.url}
              </a>
            )}
            <span className={styles.companyIndustry}>
              {createdCompany.industry || 'Not specified'}
            </span>
            <span className={`${styles.visibilityBadge} ${createdCompany.isPublic ? styles.publicVisibility : styles.privateVisibility}`}>
              {createdCompany.isPublic ? 'Public' : 'Private'}
            </span>
            {createdCompany.objective && (
              <span className={styles.companyObjective}>
                {createdCompany.objective}
              </span>
            )}
          </div>
          <div className={styles.companyActions}>
            <Button 
              variant="outline" 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(true);
              }}
            >
              Create Another
            </Button>
          </div>
        </div>
      ) : (
        // Show creation button
        <Button 
          variant="primarys" 
          onClick={() => setShowModal(true)}
          className={styles.createCompanyButton}
        >
          Create New Organization
        </Button>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className={styles.errorMessage}>{error}</div>}
          
          <form onSubmit={handleCreateCompany}>
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Organization Name *</label>
                  <input
                    type="text"
                    value={companyData.name}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, name: e.target.value }))}
                    className={styles.formInput}
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Website</label>
                  <input
                    type="url"
                    value={companyData.url}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, url: e.target.value }))}
                    className={styles.formInput}
                  />
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  <input
                    type="text"
                    value={companyData.industry}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, industry: e.target.value }))}
                    className={styles.formInput}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Organization Size</label>
                  <input
                    type="text"
                    value={companyData.metadata.size}
                    onChange={(e) => setCompanyData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, size: e.target.value }
                    }))}
                    className={styles.formInput}
                  />
                </div>
              </Col>
            </Row>

            <div className={styles.formGroup}>
              <label>Objective</label>
              <textarea
                value={companyData.objective}
                onChange={(e) => setCompanyData(prev => ({ ...prev, objective: e.target.value }))}
                className={styles.formTextarea}
                rows={3}
                placeholder="What is the main objective of this company?"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={companyData.isPublic}
                  onChange={(e) => setCompanyData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className={styles.checkbox}
                />
                Publicly Discoverable
              </label>
              <small className={styles.helpText}>
                Public organizations can be discovered and joined by other users
              </small>
            </div>

            <div className={styles.modalActions}>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Organization'}
              </Button>
              <Button variant="outline-neutral" type="button" onClick={handleCloseModal}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CompanyCreationButton;
